<script setup lang="ts">
import { ref, shallowRef, onMounted, onUnmounted } from "vue";
import { useRafFn } from "@vueuse/core";
import type { EncounterMeta, Pull, WorkerIn, WorkerOut } from "./lib/types";
import EncounterList from "./components/EncounterList.vue";
import Replay from "./components/Replay.vue";
import Timeline from "./components/Timeline.vue";
import Meters from "./components/Meters.vue";
import Duration from "./components/Duration.vue";
import {
  supportsFileSystemAccess, restoreWowDirectory,
  verifyReadPermission, listCombatLogs, readCombatLog, directoryFromDrop,
  type CombatLogFile,
} from "./lib/wow-folder";

const status = ref<"loading" | "needs-folder" | "unsupported" | "parsing" | "ready" | "error">("loading");
const detail = ref("");
const encounters = shallowRef<EncounterMeta[]>([]);
const selected = ref<number | null>(null);
const pull = shallowRef<Pull | null>(null);

// The user's WoW folder (File System Access). Picked once, remembered across
// visits; combat logs are read locally — nothing is uploaded.
const wowDir = shallowRef<FileSystemDirectoryHandle | null>(null);
const wowDirName = ref("");
const logs = shallowRef<CombatLogFile[]>([]);
const currentLog = ref("");
const needsReconnect = ref(false); // saved folder, but read permission must be re-granted by a click
const dragging = ref(false); // a folder is being dragged over the drop zone

const time = ref(0); // ms into the pull
const playing = ref(false);
const speed = ref(1);

let worker: Worker;

// Advance the clock each animation frame while playing. The delta is clamped
// so a stall (backgrounded tab, OS sleep, long GC) resumes smoothly instead of
// leaping the clock to the end in one frame.
useRafFn(({ delta }) => {
  const p = pull.value;
  if (!playing.value || !p) return;
  time.value = Math.min(p.meta.duration_ms, time.value + Math.min(delta, 100) * speed.value);
  if (time.value >= p.meta.duration_ms) playing.value = false;
});

onMounted(() => {
  worker = new Worker(new URL("./worker/parser.worker.ts", import.meta.url), { type: "module" });
  worker.onmessage = ({ data: m }: MessageEvent<WorkerOut>) => {
    if (m.type === "encounters") {
      encounters.value = m.encounters;
      status.value = "ready";
      detail.value = `${m.encounters.length} encounter(s) · parsed in ${m.parseMs} ms`;
      if (m.encounters.length) select(m.encounters[0].index);
    } else if (m.type === "pull") {
      if (m.index !== selected.value) return; // stale response from a prior selection
      pull.value = m.pull;
      time.value = 0;
      playing.value = false;
    }
  };

  initFromSavedFolder();
});

onUnmounted(() => {
  worker?.terminate();
});

// On load, reuse the previously-picked WoW folder if we still have permission;
// otherwise prompt the user to (re)connect it.
async function initFromSavedFolder() {
  if (!supportsFileSystemAccess()) {
    status.value = "unsupported";
    return;
  }
  const saved = await restoreWowDirectory();
  if (saved && (await verifyReadPermission(saved, false))) {
    useDirectory(saved);
  } else if (saved) {
    wowDir.value = saved;
    wowDirName.value = saved.name;
    needsReconnect.value = true;
    status.value = "needs-folder";
  } else {
    status.value = "needs-folder";
  }
}

// Bring back the drop zone to connect a different folder (drag-and-drop is the
// only connect path — showDirectoryPicker is blocked for Program Files installs).
function changeFolder() {
  status.value = "needs-folder";
}

// Re-grant permission to the already-saved folder (a click handler).
async function reconnect() {
  if (wowDir.value && (await verifyReadPermission(wowDir.value, true))) useDirectory(wowDir.value);
}

// Drag-and-drop a folder — the only way in for installs Chrome won't let the
// picker open (e.g. WoW under Program Files). The drop yields a real directory
// handle, so log-watching + persistence work exactly like the picker.
async function onDrop(e: DragEvent) {
  dragging.value = false;
  const handle = await directoryFromDrop(e);
  if (!handle) {
    status.value = "error";
    detail.value = "Drop your _retail_ (or Logs) folder — a single file won't keep up as WoW writes new logs.";
    return;
  }
  if (!(await verifyReadPermission(handle, true))) {
    status.value = "error";
    detail.value = "Read permission denied for that folder.";
    return;
  }
  useDirectory(handle);
}

// Scan the folder for combat logs and load the newest.
async function useDirectory(handle: FileSystemDirectoryHandle) {
  wowDir.value = handle;
  wowDirName.value = handle.name;
  needsReconnect.value = false;
  status.value = "parsing";
  detail.value = "scanning for combat logs…";
  const found = await listCombatLogs(handle);
  logs.value = found;
  if (!found.length) {
    status.value = "needs-folder";
    detail.value =
      "No WoWCombatLog*.txt in that folder. Pick your _retail_ folder (it contains Logs/), and enable Advanced Combat Logging in-game (type /combatlog).";
    return;
  }
  loadLogFile(found[0]);
}

async function loadLogFile(file: CombatLogFile) {
  try {
    status.value = "parsing";
    currentLog.value = file.name;
    detail.value = `reading ${file.name}…`;
    const text = await readCombatLog(file);
    detail.value = "parsing log…";
    worker.postMessage({ type: "parse", text } satisfies WorkerIn);
  } catch (err) {
    status.value = "error";
    detail.value = String(err);
  }
}

// Switch which log file is shown (the header dropdown).
function selectLog(name: string) {
  const file = logs.value.find((l) => l.name === name);
  if (file) loadLogFile(file);
}

function select(i: number) {
  selected.value = i;
  pull.value = null;
  playing.value = false;
  worker.postMessage({ type: "pull", index: i } satisfies WorkerIn);
}
</script>

<template>
  <div class="flex h-screen flex-col bg-zinc-950 text-zinc-100">
    <header class="flex items-baseline gap-4 border-b border-zinc-800 px-5 py-3">
      <h1 class="text-lg font-semibold tracking-tight text-amber-400">Raidlogger</h1>
      <span class="text-xs text-zinc-500">{{ detail }}</span>

      <div
        v-if="wowDir && logs.length"
        class="ml-auto flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900/60 p-1 text-xs text-zinc-400"
      >
        <select
          v-if="logs.length > 1"
          :value="currentLog"
          class="max-w-44 cursor-pointer truncate rounded border border-zinc-700 bg-zinc-900 px-2 py-1 font-mono text-zinc-200 outline-none transition-colors hover:border-zinc-600 focus:border-amber-500/70"
          @change="selectLog(($event.target as HTMLSelectElement).value)"
        >
          <option v-for="l in logs" :key="l.name" :value="l.name">{{ l.name }}</option>
        </select>
        <span class="inline-flex items-center gap-1.5 px-1.5 text-zinc-500">
          <svg class="size-3.5 text-zinc-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          </svg>
          <span class="max-w-40 truncate">{{ wowDirName }}</span>
        </span>
        <button
          class="rounded px-2 py-1 font-medium text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
          @click="changeFolder"
        >
          Change folder
        </button>
      </div>
    </header>

    <div class="flex min-h-0 flex-1">
      <aside class="w-60 shrink-0 overflow-y-auto border-r border-zinc-800 p-2">
        <EncounterList :encounters="encounters" :selected="selected" @select="select" />
      </aside>

      <main class="flex min-w-0 flex-1 flex-col">
        <div
          v-if="status === 'error'"
          class="m-6 rounded border border-red-900 bg-red-950/50 p-4 text-sm text-red-300"
        >
          {{ detail }}
        </div>

        <div
          v-else-if="status === 'unsupported'"
          class="grid flex-1 place-items-center p-6 text-center"
        >
          <div class="max-w-md space-y-4">
            <div class="mx-auto grid size-12 place-items-center rounded-full border border-zinc-800 bg-zinc-900 text-zinc-500">
              <svg class="size-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M5.6 5.6 18.4 18.4" />
              </svg>
            </div>
            <div class="space-y-1.5 text-sm text-zinc-400">
              <p class="text-zinc-200">Raidlogger reads your WoW combat logs directly from disk.</p>
              <p>That needs the File System Access API — open this in <strong class="font-medium text-zinc-200">Chrome</strong> or <strong class="font-medium text-zinc-200">Edge</strong>.</p>
            </div>
          </div>
        </div>

        <div
          v-else-if="status === 'needs-folder'"
          class="grid flex-1 place-items-center p-6"
          @dragenter.prevent="dragging = true"
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="onDrop"
        >
          <div
            class="w-full max-w-md rounded-xl border bg-zinc-900/60 p-8 text-center shadow-xl shadow-black/30 transition-colors"
            :class="dragging ? 'border-amber-500/70 bg-amber-500/5' : 'border-zinc-800'"
          >
            <div class="mx-auto mb-5 grid size-14 place-items-center rounded-xl border border-amber-500/20 bg-amber-500/10 text-amber-400">
              <svg class="size-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <path d="M12 11v5" />
                <path d="m9.5 13.5 2.5-2.5 2.5 2.5" />
              </svg>
            </div>
            <h2 class="text-xl font-semibold tracking-tight text-zinc-100">Connect your WoW folder</h2>
            <p class="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-zinc-400">
              <strong class="font-medium text-zinc-200">Drag your
                <code class="rounded bg-zinc-800 px-1 py-0.5 font-mono text-[0.8125rem] text-zinc-300">_retail_</code>
                folder here</strong> (or its
              <code class="rounded bg-zinc-800 px-1 py-0.5 font-mono text-[0.8125rem] text-zinc-300">Logs</code>
              folder). Raidlogger reads
              <code class="rounded bg-zinc-800 px-1 py-0.5 font-mono text-[0.8125rem] text-zinc-300">WoWCombatLog*.txt</code>
              locally, follows new logs as WoW writes them, and remembers the folder — nothing
              is uploaded. Works even if WoW lives in
              <code class="rounded bg-zinc-800 px-1 py-0.5 font-mono text-[0.8125rem] text-zinc-300">Program&nbsp;Files</code>.
              Enable Advanced Combat Logging in-game with
              <code class="rounded bg-zinc-800 px-1 py-0.5 font-mono text-[0.8125rem] text-zinc-300">/combatlog</code>.
            </p>
            <div v-if="needsReconnect" class="mt-6 flex flex-col items-stretch gap-2">
              <button
                class="inline-flex items-center justify-center gap-2 rounded-md bg-amber-500 px-4 py-2.5 text-sm font-medium text-zinc-950 shadow-sm transition-colors hover:bg-amber-400"
                @click="reconnect"
              >
                <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.7 3M21 5v4h-4" />
                  <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6.7-3M3 19v-4h4" />
                </svg>
                Reconnect “{{ wowDirName }}”
              </button>
            </div>
            <p v-if="detail && status === 'needs-folder'" class="mt-4 text-xs text-zinc-500">{{ detail }}</p>
          </div>
        </div>

        <template v-else-if="pull">
          <div class="min-h-0 flex-1 p-3">
            <Replay :pull="pull" :time="time" />
          </div>

          <div class="border-t border-zinc-800 p-3">
            <div class="mb-2 flex items-center gap-3 text-sm">
              <button
                class="rounded bg-amber-500 px-3 py-1 font-medium text-zinc-950 hover:bg-amber-400"
                @click="playing = !playing"
              >
                {{ playing ? "Pause" : "Play" }}
              </button>
              <div class="flex gap-1">
                <button
                  v-for="s in [1, 2, 4]"
                  :key="s"
                  class="rounded px-2 py-1 text-xs"
                  :class="speed === s ? 'bg-zinc-700 text-white' : 'bg-zinc-900 text-zinc-400'"
                  @click="speed = s"
                >
                  {{ s }}×
                </button>
              </div>
              <span class="ml-auto font-mono text-xs text-zinc-400">
                <Duration :timestamp="time" /> / <Duration :timestamp="pull.meta.duration_ms" />
              </span>
            </div>
            <Timeline :pull="pull" :time="time" @seek="time = $event" />
          </div>
        </template>

        <div v-else class="grid flex-1 place-items-center text-sm text-zinc-500">
          {{ status === "ready" ? "Select an encounter" : detail }}
        </div>
      </main>

      <aside v-if="pull" class="w-72 shrink-0 overflow-y-auto border-l border-zinc-800 p-3">
        <Meters :pull="pull" />
      </aside>
    </div>
  </div>
</template>
